import {
  useCallback,
  useEffect,
  useId,
  useState,
  type FocusEvent,
  type MouseEvent,
  type PointerEvent,
  type ReactNode,
} from 'react'

import type { KeyValueRow } from '@/components/ui/KeyValueList'

/** What a row card says: a title, label/value rows, and an optional tertiary note under them. */
export interface RowCardContent {
  title: ReactNode
  rows?: readonly KeyValueRow[] | undefined
  /** Markup between the rows and the note: Last minute movers' five session dots. */
  extra?: ReactNode
  /** A wrapped line under the rows, in the tertiary ink: a caveat, not a fact. */
  note?: ReactNode
  /** Shown only once the card is PINNED, when it takes the pointer: "Open GCIL". */
  action?: ReactNode
}

/** The card on screen: whose it is, the anchor's box when it opened, and whether it is pinned. */
export interface RowCardOpen<K> {
  key: K
  rect: DOMRect
  content: RowCardContent
  pinned: boolean
}

/** Spread on the row, bar or tile the card describes. */
export interface RowCardAnchorProps {
  'data-card-anchor': string
  /** Present while this anchor's card is up, so its wash stays on (`row-wash`). */
  'data-card-open': '' | undefined
  'aria-describedby': string | undefined
  onPointerEnter: (event: PointerEvent) => void
  onPointerLeave: (event: PointerEvent) => void
  onClick: (event: MouseEvent) => void
  onFocus: (event: FocusEvent) => void
  onBlur: () => void
}

export interface RowCardState<K> {
  /** The panel's id, for the anchor's `aria-describedby`. */
  id: string
  open: RowCardOpen<K> | null
  /** The anchor props for one row. `content` is read when the card opens, not on every render. */
  bind: (key: K, content: () => RowCardContent) => RowCardAnchorProps
  close: () => void
}

/** A focus the keyboard caused, not a click's. A browser without the selector counts every focus. */
const isFocusVisible = (element: Element): boolean => {
  try {
    return element.matches(':focus-visible')
  } catch {
    return true
  }
}

/**
 * The Broker Analysis row card (the mock's `.brb-tip`, its `Hx` hook): ONE card for a whole table
 * or chart, describing the row, bar or tile under the pointer. Large trades' rows, PV range's bars
 * and Last minute movers' rows share it; `RowCard` draws it.
 *
 * How it opens and closes, as the mock's does:
 *  - a mouse entering an anchor shows its card at once; leaving hides it — unless it is pinned;
 *  - a click (or a tap: touch has no hover) PINS the card, which then takes the pointer, and a
 *    second click on the same anchor unpins it; a click on another anchor pins that one;
 *  - keyboard focus that is `:focus-visible` shows it, blur hides it unless pinned;
 *  - Escape, any scroll, or a press outside every anchor and the card closes it.
 *
 * Closing on scroll, rather than riding along as `useChartPopover` does, is the mock's choice: the
 * box is measured once when the card opens.
 */
export function useRowCard<K extends string>(): RowCardState<K> {
  const id = useId()
  const [open, setOpen] = useState<RowCardOpen<K> | null>(null)
  const close = useCallback(() => {
    setOpen(null)
  }, [])

  useEffect(() => {
    if (open === null) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      // Claimed, as Tooltip claims it: putting the card away must not also close a dialog under it.
      event.preventDefault()
      close()
    }
    const onPointerDown = (event: globalThis.PointerEvent) => {
      const target = event.target
      if (!(target instanceof Element)) return
      if (target.closest('[data-card-anchor]') !== null) return
      if (target.closest(`[id="${CSS.escape(id)}"]`) !== null) return
      close()
    }
    window.addEventListener('keydown', onKeyDown, true)
    window.addEventListener('scroll', close, { capture: true, passive: true })
    window.addEventListener('resize', close)
    window.addEventListener('pointerdown', onPointerDown, true)
    return () => {
      window.removeEventListener('keydown', onKeyDown, true)
      window.removeEventListener('scroll', close, { capture: true })
      window.removeEventListener('resize', close)
      window.removeEventListener('pointerdown', onPointerDown, true)
    }
  }, [open, close, id])

  const bind = (key: K, content: () => RowCardContent): RowCardAnchorProps => {
    const isOpen = open?.key === key
    return {
      'data-card-anchor': key,
      'data-card-open': isOpen ? '' : undefined,
      'aria-describedby': isOpen ? id : undefined,
      onPointerEnter: (event) => {
        if (event.pointerType !== 'mouse') return
        const rect = event.currentTarget.getBoundingClientRect()
        setOpen((current) =>
          current?.pinned === true ? current : { key, rect, content: content(), pinned: false },
        )
      },
      onPointerLeave: (event) => {
        if (event.pointerType !== 'mouse') return
        setOpen((current) =>
          current !== null && !current.pinned && current.key === key ? null : current,
        )
      },
      onClick: (event) => {
        const rect = event.currentTarget.getBoundingClientRect()
        setOpen((current) =>
          current?.key === key && current.pinned
            ? null
            : { key, rect, content: content(), pinned: true },
        )
      },
      onFocus: (event) => {
        if (!isFocusVisible(event.currentTarget)) return
        const rect = event.currentTarget.getBoundingClientRect()
        setOpen({ key, rect, content: content(), pinned: false })
      },
      onBlur: () => {
        setOpen((current) =>
          current !== null && !current.pinned && current.key === key ? null : current,
        )
      },
    }
  }

  return { id, open, bind, close }
}
